/**
 * @module Header
 * @description Represents the header component of the application.
 * @returns {JSX.Element} The header component.
 */
import React, { useContext } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { Navbar, Nav, NavDropdown, Form } from 'react-bootstrap'
import { LinkContainer } from 'react-router-bootstrap'
import { useNavigate, Routes, Route, Link } from 'react-router-dom'
import { logout } from '../actions/userActions.js'
import { logoutOwner } from '../actions/ownerActions.js'
import SearchBox from '../components/SearchBox.js'
import { ThemeContext } from './ThemeContext.js'

// Header component
const Header = () => {
  const dispatch = useDispatch()
  const navigate = useNavigate()
  const { theme, isDarkMode, setIsDarkMode } = useContext(ThemeContext)

  // Get login state
  const userLogin = useSelector((state) => state.userLogin)
  const { userInfo } = userLogin
  //
  const ownerLogin = useSelector((state) => state.ownerLogin)
  const { ownerInfo } = ownerLogin

  // Logout handler
  const logoutHandler = () => {
    if (userInfo) {
      const id = userInfo._id
      dispatch(logout(id))
    } else if (ownerInfo) {
      const id = ownerInfo._id
      dispatch(logoutOwner(id))
      navigate('/')
    } else {
      navigate('/')
    }
    // Clear cookies
    document.cookie = 'jwt=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/;'
    // Clear session and localstorage
    if (userInfo) {
      sessionStorage.removeItem('userInfo')
      localStorage.removeItem('userInfo')
    } else {
      sessionStorage.removeItem('ownerInfo')
      localStorage.removeItem('ownerInfo')
    }
  }

  return (
    <>
      <Navbar
        className='themed-header px-4 py-3 border-bottom shadow-sm'
        variant={isDarkMode ? 'dark' : 'light'}
        style={{
          '--header-text': theme.text,
          '--header-subtext': theme.subtext,
          '--header-card': theme.card,
          '--header-border': isDarkMode ? '#2D342D' : '#DFE6E9',
          '--header-hover-bg': isDarkMode ? '#2F3A33' : '#EDF3EE',
          '--header-active-bg': isDarkMode ? '#3A4A40' : '#E1EAE3',
          '--header-top-hover-bg': isDarkMode
            ? 'rgba(223, 255, 0, 0.12)'
            : 'rgba(110, 127, 107, 0.14)',
          '--header-top-active-bg': isDarkMode
            ? 'rgba(223, 255, 0, 0.2)'
            : 'rgba(110, 127, 107, 0.22)',
          '--header-focus-ring': isDarkMode
            ? 'rgba(223, 255, 0, 0.3)'
            : 'rgba(110, 127, 107, 0.35)',
          backgroundColor: theme.card,
          borderColor: isDarkMode ? '#040404' : '#DFE6E9'
        }}
        expand='lg'
      >
        {!userInfo ? (
          <Navbar.Brand
            as={Link}
            to='/'
            className='fw-bold'
            style={{ color: theme.text }}
          >
            <span style={{ color: theme.accent }}>🌿</span> Global Receipt
          </Navbar.Brand>
        ) : (
          <Navbar.Brand className='right-align'>
            <span style={{ color: theme.accent }}>🌿</span> Global Receipt
          </Navbar.Brand>
        )}

        <Navbar.Toggle aria-controls='basic-navbar-nav' />
        <Navbar.Collapse id='basic-navbar-nav'>
          {userInfo && (
            <Routes>
              <Route path='/*' id='basic-navbar-nav' element={<SearchBox />} />
            </Routes>
          )}

          <Nav className='ms-auto d-flex align-items-center gap-3'>
            <Form.Check
              type='switch'
              id='theme-switch'
              label={isDarkMode ? '🌙 Dark' : '☀️ Light'}
              checked={isDarkMode}
              onChange={() => setIsDarkMode(!isDarkMode)}
              className='small'
              style={{ color: theme.subtext }}
            />
            {!userInfo && !ownerInfo && (
              <NavDropdown
                title={<span style={{ color: theme.text }}>Menu</span>}
                id='basic-nav-dropdown'
                className='themed-nav-dropdown'
                menuVariant={isDarkMode ? 'dark' : undefined}
              >
                <LinkContainer to='/login'>
                  <NavDropdown.Item>User Login</NavDropdown.Item>
                </LinkContainer>
                <LinkContainer to='/register'>
                  <NavDropdown.Item>User Register</NavDropdown.Item>
                </LinkContainer>
                <LinkContainer to='/ownerlogin'>
                  <NavDropdown.Item>Business Login</NavDropdown.Item>
                </LinkContainer>
                <LinkContainer to='/businessregister'>
                  <NavDropdown.Item>Business Register</NavDropdown.Item>
                </LinkContainer>
                {/* <LinkContainer to='/contact'>
                  <NavDropdown.Item>Contact Us</NavDropdown.Item>
                </LinkContainer> */}
                <LinkContainer to='/about'>
                  <NavDropdown.Item>About Us</NavDropdown.Item>
                </LinkContainer>
              </NavDropdown>
            )}

            {userInfo && (
              <LinkContainer to='/receipts'>
                <Navbar.Brand className='small ml-right'>Receipts</Navbar.Brand>
              </LinkContainer>
            )}

            {userInfo && !userInfo.isAdmin && (
              <NavDropdown
                title={userInfo.name}
                id='username'
                className='themed-nav-dropdown'
                menuVariant={isDarkMode ? 'dark' : undefined}
              >
                <LinkContainer to='/dashboard'>
                  <NavDropdown.Item>Dashboard</NavDropdown.Item>
                </LinkContainer>
                <LinkContainer to='/reports'>
                  <NavDropdown.Item>Reports</NavDropdown.Item>
                </LinkContainer>
                <LinkContainer to='/profile'>
                  <NavDropdown.Item>Profile</NavDropdown.Item>
                </LinkContainer>
                <NavDropdown.Item href='mailto:info@globalreceipt.ca'>
                  Contact Us
                </NavDropdown.Item>
                <NavDropdown.Item onClick={logoutHandler}>
                  Logout
                </NavDropdown.Item>
              </NavDropdown>
            )}
            {userInfo && userInfo.isAdmin && (
              <NavDropdown
                title={userInfo.name}
                id='username'
                className='themed-nav-dropdown'
                menuVariant={isDarkMode ? 'dark' : undefined}
              >
                <LinkContainer to='/userlist'>
                  <NavDropdown.Item>Users</NavDropdown.Item>
                </LinkContainer>

                <LinkContainer to='/dashboard'>
                  <NavDropdown.Item>Dashboard</NavDropdown.Item>
                </LinkContainer>

                <LinkContainer to='/reports'>
                  <NavDropdown.Item>Reports</NavDropdown.Item>
                </LinkContainer>

                <LinkContainer to='/profile'>
                  <NavDropdown.Item>Profile</NavDropdown.Item>
                </LinkContainer>
                <NavDropdown.Item href='mailto:info@globalreceipt.ca'>
                  Contact Us
                </NavDropdown.Item>
                <NavDropdown.Item onClick={logoutHandler}>
                  Logout
                </NavDropdown.Item>
              </NavDropdown>
            )}
            {ownerInfo && (
              <NavDropdown title={ownerInfo.name} id='ownername'>
                <LinkContainer to='/ownerdashboard'>
                  <NavDropdown.Item>Dashboard</NavDropdown.Item>
                </LinkContainer>

                <LinkContainer to='/receiptchart'>
                  <NavDropdown.Item>Receipt Count Chart</NavDropdown.Item>
                </LinkContainer>

                <LinkContainer to='/ownerprofile'>
                  <NavDropdown.Item>Profile</NavDropdown.Item>
                </LinkContainer>

                <NavDropdown.Item href='mailto:info@globalreceipt.ca'>
                  Contact Us
                </NavDropdown.Item>

                <LinkContainer to='/apidocument'>
                  <NavDropdown.Item>API Document</NavDropdown.Item>
                </LinkContainer>

                <NavDropdown.Item onClick={logoutHandler}>
                  Logout
                </NavDropdown.Item>
              </NavDropdown>
            )}
          </Nav>
        </Navbar.Collapse>
      </Navbar>
    </>
  )
}

export default Header
